import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Card,
  IconChevronRight,
  PageHeader,
  Spinner,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card style={{ marginTop: 16 }}>
      <div
        className="dsp-row"
        style={{ justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}
      >
        <div style={{ fontWeight: 700 }}>{title}</div>
        {action}
      </div>
      {children}
    </Card>
  );
}

export function Overview() {
  const { data, loading, error } = useAsync(() =>
    Promise.all([
      adminApi.shifts.all(),
      adminApi.credentials.queue('pending'),
      adminApi.professionals.list(),
      adminApi.invitations.list('pending'),
    ])
  );

  if (loading) return <Spinner label="Loading overview…" />;
  if (error || !data) {
    return (
      <Card>
        <span style={{ color: 'var(--dsp-danger-fg)' }}>
          Couldn’t load overview{error ? `: ${error}` : ''}.
        </span>
      </Card>
    );
  }

  const [shifts, pendingCreds, pros, pendingInvites] = data;
  const prosPending = pros.filter((p) => p.status === 'pending');
  const awaiting = shifts
    .filter((s) => s.status === 'pending_confirmation' || s.status === 'open')
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, 6);

  return (
    <div>
      <PageHeader title="Home" />
      <Section title="Operations workspaces">
        <div className="dsp-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <Link to="/review-moderation" className="dsp-btn dsp-btn--secondary dsp-btn--md">
            Review moderation
          </Link>
          <Link to="/support" className="dsp-btn dsp-btn--secondary dsp-btn--md">
            Support queue
          </Link>
          <Link to="/payout-onboarding" className="dsp-btn dsp-btn--secondary dsp-btn--md">
            Payout onboarding
          </Link>
          <Link to="/tax-documents" className="dsp-btn dsp-btn--secondary dsp-btn--md">
            Tax documents
          </Link>
        </div>
      </Section>
      {pendingCreds.length > 0 ? (
        <Section
          title="Credentials to review"
          action={
            <Link to="/credentials" className="dsp-btn dsp-btn--ghost dsp-btn--sm">
              View all
            </Link>
          }
        >
          <Stack gap={0}>
            {pendingCreds.slice(0, 5).map((c) => (
              <Link
                key={c.id}
                to="/credentials"
                className="dsp-row"
                style={{
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--dsp-border)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {c.professionalName} · {humanize(c.type)}
                  </div>
                  <div className="dsp-muted" style={{ fontSize: '0.86rem', marginTop: 2 }}>
                    {c.label || c.number || 'Document submitted'}
                  </div>
                </div>
                <div className="dsp-row" style={{ gap: 8 }}>
                  <Badge tone="warning">pending</Badge>
                  <IconChevronRight size={16} />
                </div>
              </Link>
            ))}
          </Stack>
        </Section>
      ) : null}

      {prosPending.length > 0 ? (
        <Section
          title="People to approve"
          action={
            <Link to="/professionals" className="dsp-btn dsp-btn--ghost dsp-btn--sm">
              Open list
            </Link>
          }
        >
          <div className="dsp-muted">{prosPending.length} professional profiles need review.</div>
        </Section>
      ) : null}

      {awaiting.length > 0 ? (
        <Section
          title="Shifts"
          action={
            <Link to="/operations" className="dsp-btn dsp-btn--ghost dsp-btn--sm">
              View all
            </Link>
          }
        >
          <Stack gap={0}>
            {awaiting.map((s) => (
              <Link
                key={s.id}
                to="/operations"
                className="dsp-row"
                style={{
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--dsp-border)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {humanize(s.roleRequired)} · {s.locationName}
                  </div>
                  <div className="dsp-muted" style={{ fontSize: '0.86rem', marginTop: 2 }}>
                    {new Date(s.startsAt).toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    {s.bookingCount > 0 ? ` · ${s.bookingCount} booking(s)` : ''}
                  </div>
                </div>
                <Badge tone={s.status === 'pending_confirmation' ? 'warning' : 'info'}>
                  {humanize(s.status)}
                </Badge>
              </Link>
            ))}
          </Stack>
        </Section>
      ) : null}

      {pendingInvites.length > 0 ? (
        <Section
          title="Pending invites"
          action={
            <Link to="/invites" className="dsp-btn dsp-btn--ghost dsp-btn--sm">
              View all
            </Link>
          }
        >
          <Stack gap={0}>
            {pendingInvites.slice(0, 5).map((inv) => (
              <Link
                key={inv.id}
                to="/invites"
                className="dsp-row"
                style={{
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--dsp-border)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{inv.email}</div>
                  <div className="dsp-muted" style={{ fontSize: '0.86rem', marginTop: 2 }}>
                    {humanize(inv.role)}
                  </div>
                </div>
                <div className="dsp-row" style={{ gap: 8 }}>
                  <Badge tone="warning">pending</Badge>
                  <IconChevronRight size={16} />
                </div>
              </Link>
            ))}
          </Stack>
        </Section>
      ) : null}
      {pendingCreds.length === 0 &&
      prosPending.length === 0 &&
      awaiting.length === 0 &&
      pendingInvites.length === 0 ? (
        <Card style={{ marginTop: 16 }}>
          No staffing or credential reviews in this overview. Check the operations workspaces for
          payout, tax-document and support requests.
        </Card>
      ) : null}
    </div>
  );
}
